import { type FC, useState, useEffect, useMemo, useRef } from "react";
import * as Sentry from "@sentry/react";
import {
  createEmptyUtilityContractDetails,
  useContract,
} from "../../contexts/ContractContext";
import {
  Button,
  Alert,
  Badge,
  TextInput,
  Pagination,
  ToggleSwitch,
} from "flowbite-react";
import {
  getContractCategories,
  type ContractCategory,
} from "../../services/contract-categories";
import {
  type ContractType,
  type ContractCompany,
  getDetailsForContractTypes,
  getContractTypesPage,
} from "../../services/contract-types";
import { getContractCompanies } from "../../services/contract-companies";
import ContractTypeCard from "./ContractTypeCard";
import SignaturePad from "./SignaturePad";
import ContractConfirmationModal from "./ContractConfirmationModal";
import ContractSuccessModal from "./ContractSuccessModal";
import {
  saveContract,
  saveElectricityContractDetails,
  saveGasContractDetails,
} from "../../services/contracts";
import { getWorkerIdForUser } from "../../services/workers";
import { HiX, HiSearch } from "react-icons/hi";
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";

import {
  generateContractPdf,
  determinePdfTemplate,
} from "../../lib/pdf/contractPdfGenerator";
import { sendContractEmailBatchWithManagementPriority } from "../../lib/email/resendClient";
import { getCurrentDateFormatted } from "../../lib/pdf/fieldMapper";
import type { ContractPdfData } from "../../types/pdf";
import { downloadSignatureAsBase64 } from "../../utils/signatureStorage";
import {
  getCachedCategories,
  setCachedCategories,
  isCategoriesCacheValid,
  getCachedCompanies,
  setCachedCompanies,
  isCompaniesCacheValid,
  getUserPreferredCategory,
  getUserPreferredContractFilters,
  setUserPreferredContractFilters,
  type WorkerSignatureData,
} from "../../utils/contractDataCache";
import { getWorkerData } from "../../utils/workerData";
import {
  clearContractCardDraft,
  getContractCardDraft,
  isUpdateReloadSessionActive,
  saveContractCardDraft,
} from "../../utils/updateReloadDraft";
import { MultiSelect } from "../MultiSelect";
import { readStorageItem, writeStorageItem } from "../../utils/safeStorage";

// Helper function to validate birth date and check if person is 18+ years old
const validateBirthDateAge = (
  birthDateString: string,
  minAge: number = 18
): { isValid: boolean; message: string; age?: number } => {
  if (!birthDateString) {
    return { isValid: false, message: "Geburtsdatum erforderlich" };
  }

  try {
    // Handle dd.mm.yyyy format
    let dateObj: Date;
    if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(birthDateString)) {
      const parts = birthDateString.split(".");
      if (parts.length !== 3) {
        return { isValid: false, message: "Ungültiges Datumsformat" };
      }
      const [day, month, year] = parts;
      // JavaScript months are 0-indexed
      dateObj = new Date(
        parseInt(year || "0"),
        parseInt(month || "0") - 1,
        parseInt(day || "0")
      );
    } else {
      // Handle yyyy-mm-dd format from date picker
      dateObj = new Date(birthDateString);
    }

    if (isNaN(dateObj.getTime())) {
      return { isValid: false, message: "Ungültiges Datum" };
    }

    // Calculate age
    const today = new Date();
    let age = today.getFullYear() - dateObj.getFullYear();
    const monthDiff = today.getMonth() - dateObj.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < dateObj.getDate())
    ) {
      age--;
    }

    // Validate minimum age
    if (age < minAge) {
      return {
        isValid: false,
        message: `Das Mindestalter von ${minAge} Jahren ist nicht erreicht`,
        age,
      };
    }

    // Future date check
    if (dateObj > today) {
      return { isValid: false, message: "Geburtsdatum liegt in der Zukunft" };
    }

    return { isValid: true, message: "", age };
  } catch (error) {
    return { isValid: false, message: "Fehler bei der Datumsvalidierung" };
  }
};

const ContractCard: FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const {
    contractData,
    updateContractData,
    tempAddress,
    addContractType,
    removeContractType,
  } = useContract();
  const [categories, setCategories] = useState<ContractCategory[]>([]);
  const [companies, setCompanies] = useState<ContractCompany[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contractTypes, setContractTypes] = useState<
    (ContractType & { company_details: ContractCompany })[]
  >([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [customerSignatureDataUrl, setCustomerSignatureDataUrl] = useState<
    string | null
  >(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [bankDetailsFocused, setBankDetailsFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [hasRestoredPreferredFilters, setHasRestoredPreferredFilters] =
    useState(false);
  const [totalContractTypes, setTotalContractTypes] = useState(0);
  const [isGlobalSearchFallback, setIsGlobalSearchFallback] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9; // Show 9 items per page (3x3 grid)
  // PDF and email states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalMessage, setSuccessModalMessage] = useState("");
  const [successModalSuccess, setSuccessModalSuccess] = useState(true);
  const [successModalActionLabel, setSuccessModalActionLabel] = useState<
    string | undefined
  >(undefined);

  // State to track successfully saved customer data to prevent duplicates on retry
  const [savedCustomerData, setSavedCustomerData] = useState<{
    customer: { id: number };
    contract: { id: number };
  } | null>(null);
  const [savedContractRefs, setSavedContractRefs] = useState<
    | { contractTypeId: number; contractType: string; contractId: number }[]
    | null
  >(null);

  // Birth date state for text input - initialize from localStorage if available
  const [useDateTextInput, setUseDateTextInput] = useState<boolean>(() => {
    const savedPreference = readStorageItem("birthDateInputPreference");
    return savedPreference ? savedPreference === "text" : false;
  });
  const [birthDateText, setBirthDateText] = useState("");

  // Birth date validation state
  const [birthDateValidationError, setBirthDateValidationError] = useState<
    string | null
  >(null);

  const electricityDetails = contractData.utilityDetails.electricity;
  const gasDetails = contractData.utilityDetails.gas;
  const setElectricityDetails = (details: typeof electricityDetails) => {
    updateContractData({
      utilityDetails: {
        electricity: details,
      },
    });
  };
  const setGasDetails = (details: typeof gasDetails) => {
    updateContractData({
      utilityDetails: {
        gas: details,
      },
    });
  };

  // Date validation states
  const [electricityDateError, setElectricityDateError] = useState<
    string | null
  >(null);
  const [gasDateError, setGasDateError] = useState<string | null>(null);

  // Worker data state (for sales partner company, employee name, and signature)
  const [workerData, setWorkerData] = useState<WorkerSignatureData | null>(
    null
  );
  const hasAttemptedDraftRestoreRef = useRef(false);
  const contractTypesRequestIdRef = useRef(0);

  // Validation function to check if date is after today
  const validateDeliveryDate = (dateString: string): boolean => {
    if (!dateString) return true; // Allow empty for now, will be caught by required validation
    const selectedDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison
    return selectedDate > today;
  };

  // Helper function to check if a contract type is a gas contract
  // Matches: "gas" as separate word, or "erdgas"/"naturgas" anywhere in the name
  const isGasContract = (contractTypeName: string): boolean => {
    const lowerName = contractTypeName.toLowerCase();

    // Check if contains "erdgas" anywhere (e.g., "Erdgas", "Erdgas24", "Erdgasfix24")
    if (lowerName.includes("erdgas")) {
      return true;
    }

    // Check if contains "naturgas" anywhere (e.g., "Naturgas", "Naturgas24")
    if (lowerName.includes("naturgas")) {
      return true;
    }

    // Check if "gas" appears as a standalone word (word boundary check)
    // Matches "Gas", "Gasag Gas Fix 24" but NOT "Megastream"
    if (/\bgas\b/i.test(contractTypeName)) {
      return true;
    }

    return false;
  };

  const isStadtwerkeDessauCategory = (category: string): boolean =>
    category.toLowerCase().includes("stadtwerke dessau");

  const getRequiredUtilityType = (
    contractTypeName: string,
    contractCategory: string
  ): "electricity" | "gas" | "none" => {
    if (!isStadtwerkeDessauCategory(contractCategory)) {
      return "none";
    }
    if (isGasContract(contractTypeName)) {
      return "gas";
    }
    if (contractTypeName.toLowerCase().includes("strom")) {
      return "electricity";
    }
    return "none";
  };

  // Helper function to check if a value is filled (handles strings and numbers)
  const isFilled = (value: string | number): boolean => {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return value !== null && value !== undefined && !isNaN(Number(value));
  };

  const isValidSelectedContractType = (
    contractType: unknown
  ): contractType is ContractType & { company_details: ContractCompany } => {
    if (!contractType || typeof contractType !== "object") return false;
    const record = contractType as Partial<ContractType> & {
      company_details?: Partial<ContractCompany>;
    };

    return (
      typeof record.id === "number" &&
      typeof record.type === "string" &&
      record.type.trim() !== "" &&
      typeof record.category === "string" &&
      record.category.trim() !== "" &&
      typeof record.company_details?.company === "string" &&
      record.company_details.company.trim() !== "" &&
      typeof record.company_details?.color_code === "string" &&
      record.company_details.color_code.trim() !== ""
    );
  };

  const getInvalidSelectedContractType = () =>
    contractData.selectedContractTypes.find(
      (contractType) => !isValidSelectedContractType(contractType)
    );

  const getValidatedBirthDateForSave = ():
    | { birthdate: string; error: null }
    | { birthdate: null; error: string } => {
    const rawBirthDate = useDateTextInput
      ? birthDateText
      : contractData.contract.birthDate;
    const validation = validateBirthDateAge(rawBirthDate);

    if (!validation.isValid) {
      return { birthdate: null, error: validation.message };
    }

    const formattedBirthDate = formatBirthDateForDatabase(rawBirthDate);
    if (!formattedBirthDate) {
      return { birthdate: null, error: "Ungültiges Geburtsdatum" };
    }

    return { birthdate: formattedBirthDate, error: null };
  };

  // Helper function to check if birth date is missing
  const isBirthDateMissing = (): boolean => {
    // Check which input method is used
    if (useDateTextInput) {
      return birthDateText.trim() === "";
    }
    return contractData.contract.birthDate === "";
  };

  // Calculate default birth date (18 years ago from today) for date picker
  const defaultBirthDate = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 18);
    return date.toISOString().split("T")[0]; // Format: YYYY-MM-DD
  }, []);

  // Helper function to check if electricity details are filled (at least the required fields)
  const hasElectricityDetailsFilled = (): boolean => {
    return (
      isFilled(electricityDetails.previous_provider_name) &&
      isFilled(electricityDetails.previous_customer_number) &&
      isFilled(electricityDetails.annual_consumption_kwh) &&
      isFilled(electricityDetails.meter_number) &&
      isFilled(electricityDetails.unit_price_ct_kwh) &&
      isFilled(electricityDetails.base_price_eur_month) &&
      // Date is required ONLY if "as soon as possible" is NOT checked
      (electricityDetails.as_soon_as_possible ||
        isFilled(electricityDetails.delivery_start_date))
    );
  };

  // Helper function to check if gas details are filled (at least the required fields)
  const hasGasDetailsFilled = (): boolean => {
    return (
      isFilled(gasDetails.previous_provider_name) &&
      isFilled(gasDetails.previous_customer_number) &&
      isFilled(gasDetails.annual_consumption_kwh) &&
      isFilled(gasDetails.meter_number) &&
      isFilled(gasDetails.unit_price_ct_kwh) &&
      isFilled(gasDetails.base_price_eur_month) &&
      // Date is required ONLY if "as soon as possible" is NOT checked
      (gasDetails.as_soon_as_possible ||
        isFilled(gasDetails.delivery_start_date))
    );
  };

  const getMissingRequiredUtilityDetail = (
    selectedTypes: (ContractType & { company_details: ContractCompany })[]
  ): { detailType: "gas" | "electricity"; contractType: string } | null => {
    for (const selectedType of selectedTypes) {
      const requiredType = getRequiredUtilityType(
        selectedType.type,
        selectedType.category
      );
      if (requiredType === "gas" && !hasGasDetailsFilled()) {
        return { detailType: "gas", contractType: selectedType.type };
      }
      if (requiredType === "electricity" && !hasElectricityDetailsFilled()) {
        return { detailType: "electricity", contractType: selectedType.type };
      }
    }
    return null;
  };

  const convertToNumber = (value: string | number | undefined): number => {
    if (value === undefined || value === null || value === "") {
      return 0;
    }
    const num = typeof value === "string" ? parseFloat(value) : value;
    return Number.isNaN(num) ? 0 : num;
  };

  const convertToNullableNumber = (
    value: string | number | undefined
  ): number | null => {
    if (value === undefined || value === null || value === "") {
      return null;
    }
    const num = typeof value === "string" ? parseFloat(value) : value;
    return Number.isNaN(num) ? null : num;
  };

  const convertToNullableString = (
    value: string | undefined
  ): string | null => {
    if (!value || value.trim() === "") {
      return null;
    }
    return value.trim();
  };

  // Save preference when toggle changes
  const handleToggleChange = (checked: boolean) => {
    setUseDateTextInput(checked);
    writeStorageItem("birthDateInputPreference", checked ? "text" : "date");
  };

  // Format birth date for display
  const formatBirthDateForDisplay = (dateString: string): string => {
    try {
      if (!dateString) return "";

      // Check if already in dd.mm.yyyy format
      if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateString)) {
        // Ensure 2 digit day and month
        const parts = dateString.split(".");
        if (parts.length !== 3) return dateString;

        const day = parts[0] || "";
        const month = parts[1] || "";
        const year = parts[2] || "";

        return `${day.padStart(2, "0")}.${month.padStart(2, "0")}.${year}`;
      }

      const date = new Date(dateString);
      if (isNaN(date.getTime())) return ""; // Invalid date

      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  // Format birth date for database (yyyy-mm-dd)
  const formatBirthDateForDatabase = (dateString: string): string => {
    try {
      if (!dateString) return "";

      // If in dd.mm.yyyy format
      if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateString)) {
        // Parse from dd.mm.yyyy to Date object
        const parts = dateString.split(".");
        if (parts.length !== 3) return "";

        const day = parts[0] || "";
        const month = parts[1] || "";
        const year = parts[2] || "";

        const formattedDay = day.padStart(2, "0");
        const formattedMonth = month.padStart(2, "0");

        // Create date string in yyyy-mm-dd format
        const dateStr = `${year}-${formattedMonth}-${formattedDay}`;
        const date = new Date(dateStr);

        if (isNaN(date.getTime())) return ""; // Invalid date

        // Format as yyyy-mm-dd
        const isoDate = date.toISOString();
        return isoDate.substring(0, 10); // Get yyyy-mm-dd part
      }

      // If already in a standard date format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return ""; // Invalid date

      const isoDate = date.toISOString();
      return isoDate.substring(0, 10); // Get yyyy-mm-dd part
    } catch (error) {
      console.error("Error formatting date for database:", error);
      return "";
    }
  };

  // Auto-correct date format
  const handleBirthDateTextChange = (value: string) => {
    setBirthDateText(value);

    // Auto-correct format if user types d.m.yyyy
    if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(value)) {
      const parts = value.split(".");
      if (parts.length !== 3) return;

      const day = parts[0] || "";
      const month = parts[1] || "";
      const year = parts[2] || "";

      const formattedDay = day.padStart(2, "0");
      const formattedMonth = month.padStart(2, "0");

      if (day !== formattedDay || month !== formattedMonth) {
        setBirthDateText(`${formattedDay}.${formattedMonth}.${year}`);
        return; // Return early to avoid validating unformatted input
      }
    }

    // Validate age when text input is complete (dd.mm.yyyy format)
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
      const validation = validateBirthDateAge(value);
      if (!validation.isValid) {
        setBirthDateValidationError(validation.message);
      } else {
        setBirthDateValidationError(null);
      }
    } else if (value === "") {
      setBirthDateValidationError(null); // Clear error when field is empty
    }

    // Update contract data
    updateContractData({
      contract: {
        ...contractData.contract,
        birthDate: value,
      },
    });
  };

  // Initialize birth date text from contract data when component mounts
  useEffect(() => {
    if (contractData.contract.birthDate) {
      setBirthDateText(
        formatBirthDateForDisplay(contractData.contract.birthDate)
      );
    }
  }, []);

  // Fetch worker data for logged-in user with lazy caching
  useEffect(() => {
    const fetchWorkerData = async () => {
      if (!user?.id) {
        console.log("No user ID available for worker data fetch");
        return;
      }

      const data = await getWorkerData(user.id);
      if (data) {
        setWorkerData(data);
      }
    };

    fetchWorkerData();
  }, [user?.id]);

  // Restore one-time draft after a version-triggered reload.
  useEffect(() => {
    if (hasAttemptedDraftRestoreRef.current) {
      return;
    }
    hasAttemptedDraftRestoreRef.current = true;

    if (!isUpdateReloadSessionActive()) {
      return;
    }

    const draft = getContractCardDraft();
    if (!draft) {
      return;
    }

    updateContractData(draft.contractData);
    setBirthDateText(draft.birthDateText);
    setUseDateTextInput(draft.useDateTextInput);
    setElectricityDetails(draft.electricityDetails);
    setGasDetails(draft.gasDetails);
    setSearchQuery(draft.searchQuery);
    setCurrentPage(draft.currentPage);
    setCustomerSignatureDataUrl(draft.customerSignatureDataUrl);
    setBirthDateValidationError(null);
    setSaveError(null);
    showNotification("Formulardaten nach Update wiederhergestellt", "info");
  }, [showNotification, updateContractData]);

  // Persist contract draft continuously so an update-triggered reload can recover it.
  useEffect(() => {
    saveContractCardDraft({
      timestamp: Date.now(),
      contractData,
      birthDateText,
      useDateTextInput,
      electricityDetails,
      gasDetails,
      searchQuery,
      currentPage,
      customerSignatureDataUrl,
    });
  }, [
    contractData,
    birthDateText,
    useDateTextInput,
    electricityDetails,
    gasDetails,
    searchQuery,
    currentPage,
    customerSignatureDataUrl,
  ]);

  const hasActiveProductFilters =
    selectedCategories.length > 0 || selectedCompanies.length > 0;
  const hasSearchInput = searchQuery.trim().length > 0;
  const hasDebouncedSearchQuery = debouncedSearchQuery.trim().length > 0;

  // Load contract categories and companies on mount (using cache)
  useEffect(() => {
    const loadData = async () => {
      setHasRestoredPreferredFilters(false);
      try {
        let categoriesData: ContractCategory[] = [];
        let companiesData: ContractCompany[] = [];

        // Try to get categories from cache first
        const cachedCategories = getCachedCategories();
        if (cachedCategories && isCategoriesCacheValid()) {
          categoriesData = cachedCategories;
        } else {
          // Fetch fresh data if cache miss or expired
          categoriesData = await getContractCategories();
          setCachedCategories(categoriesData);
        }

        // Try to get companies from cache first
        const cachedCompanies = getCachedCompanies();
        if (cachedCompanies && isCompaniesCacheValid()) {
          companiesData = cachedCompanies;
        } else {
          // Fetch fresh data if cache miss or expired
          companiesData = await getContractCompanies();
          setCachedCompanies(companiesData);
        }

        setCategories(categoriesData);
        setCompanies(companiesData);

        // Restore the user's preferred multi-filter selection if available.
        if (user?.id) {
          const preferredFilters = getUserPreferredContractFilters(user.id);
          const restoredCategories =
            preferredFilters?.categories.filter((category) =>
              categoriesData.some((item) => item.category === category)
            ) ?? [];
          const restoredCompanies =
            preferredFilters?.companies.filter((company) =>
              companiesData.some((item) => item.company === company)
            ) ?? [];

          if (restoredCategories.length > 0 || restoredCompanies.length > 0) {
            setSelectedCategories(restoredCategories);
            setSelectedCompanies(restoredCompanies);
          } else {
            const preferredCategory = getUserPreferredCategory(user.id);
            if (
              preferredCategory &&
              categoriesData.some((cat) => cat.category === preferredCategory)
            ) {
              setSelectedCategories([preferredCategory]);
            }
          }
        }
      } catch (err) {
        setError("Fehler beim Laden der Daten");
        console.error(err);
      } finally {
        setHasRestoredPreferredFilters(true);
        setIsLoading(false);
      }
    };

    loadData();
  }, [user?.id]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategories, selectedCompanies, debouncedSearchQuery]);

  useEffect(() => {
    if (!hasRestoredPreferredFilters || !user?.id) {
      return;
    }

    setUserPreferredContractFilters(user.id, {
      categories: selectedCategories,
      companies: selectedCompanies,
    });
  }, [
    hasRestoredPreferredFilters,
    selectedCategories,
    selectedCompanies,
    user?.id,
  ]);

  // Load contract types when filters or search change.
  useEffect(() => {
    const loadContractTypes = async () => {
      if (!hasRestoredPreferredFilters) {
        return;
      }

      if (!hasActiveProductFilters && !hasDebouncedSearchQuery) {
        contractTypesRequestIdRef.current += 1;
        setContractTypes([]);
        setTotalContractTypes(0);
        setIsGlobalSearchFallback(false);
        setLoadingTypes(false);
        return;
      }

      const requestId = ++contractTypesRequestIdRef.current;
      setLoadingTypes(true);
      try {
        let result = await getContractTypesPage({
          page: currentPage,
          pageSize: itemsPerPage,
          categories: selectedCategories,
          companies: selectedCompanies,
          searchTerm: debouncedSearchQuery,
          status: "active",
        });
        let usingGlobalFallback = false;

        if (
          hasDebouncedSearchQuery &&
          hasActiveProductFilters &&
          result.total === 0
        ) {
          result = await getContractTypesPage({
            page: currentPage,
            pageSize: itemsPerPage,
            searchTerm: debouncedSearchQuery,
            status: "active",
          });
          usingGlobalFallback = true;
        }

        if (requestId !== contractTypesRequestIdRef.current) {
          return;
        }

        const totalPages = Math.max(1, Math.ceil(result.total / itemsPerPage));
        if (currentPage > totalPages) {
          setCurrentPage(totalPages);
          return;
        }

        const contractTypesWithDetails = await getDetailsForContractTypes(
          result.data
        );

        if (requestId !== contractTypesRequestIdRef.current) {
          return;
        }

        setContractTypes(contractTypesWithDetails);
        setTotalContractTypes(result.total);
        setIsGlobalSearchFallback(usingGlobalFallback);
      } catch (err) {
        console.error("Fehler beim Laden der Produkte:", err);
        if (requestId === contractTypesRequestIdRef.current) {
          setContractTypes([]);
          setTotalContractTypes(0);
          setIsGlobalSearchFallback(false);
        }
      } finally {
        if (requestId === contractTypesRequestIdRef.current) {
          setLoadingTypes(false);
        }
      }
    };

    loadContractTypes();
  }, [
    currentPage,
    debouncedSearchQuery,
    hasActiveProductFilters,
    hasDebouncedSearchQuery,
    hasRestoredPreferredFilters,
    selectedCategories,
    selectedCompanies,
  ]);

  // Calculate pagination
  const totalPages = Math.ceil(totalContractTypes / itemsPerPage);

  const categoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        value: category.category,
        label: category.category,
      })),
    [categories]
  );

  const companyOptions = useMemo(
    () =>
      companies.map((company) => ({
        value: company.company,
        label: company.company,
      })),
    [companies]
  );

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof typeof contractData.contract
  ) => {
    updateContractData({
      contract: {
        ...contractData.contract,
        [field]: e.target.value,
      },
    });
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedCompanies([]);
    setCurrentPage(1);
  };

  const handleSelectedCategoriesChange = (categories: string[]) => {
    setSelectedCategories(categories);
    setCurrentPage(1);
  };

  const handleSelectedCompaniesChange = (companies: string[]) => {
    setSelectedCompanies(companies);
    setCurrentPage(1);
  };

  const handleSignatureSave = (signaturePath: string, dataUrl: string) => {
    console.log(
      "ContractCard: handleSignatureSave called with path:",
      signaturePath
    );
    updateContractData({
      contract: {
        ...contractData.contract,
        signature_path: signaturePath,
      },
    });
    setCustomerSignatureDataUrl(dataUrl);
    console.log("ContractCard: contract data updated and signature cached");
    setShowSignaturePad(false);
    console.log("ContractCard: signature pad closed");
    setSaveError(null);
  };

  const handleSaveClick = () => {
    if (!tempAddress?.id || contractData.selectedContractTypes.length === 0) {
      setSaveError(
        contractData.selectedContractTypes.length === 0
          ? "Bitte wählen Sie mindestens ein Produkt aus"
          : "Adress-ID fehlt"
      );
      return;
    }

    const invalidContractType = getInvalidSelectedContractType();
    if (invalidContractType) {
      setSaveError(
        "Ein ausgewähltes Produkt ist unvollständig. Bitte entfernen Sie es und wählen Sie es erneut aus."
      );
      return;
    }

    const birthDateResult = getValidatedBirthDateForSave();
    if (birthDateResult.error) {
      setBirthDateValidationError(birthDateResult.error);
      setSaveError(birthDateResult.error);
      return;
    }

    if (birthDateValidationError) {
      setSaveError(birthDateValidationError);
      return;
    }

    const missingRequiredDetail = getMissingRequiredUtilityDetail(
      contractData.selectedContractTypes
    );
    if (missingRequiredDetail) {
      setSaveError(
        missingRequiredDetail.detailType === "gas"
          ? `Bitte füllen Sie die Gas-Vertragsdaten für "${missingRequiredDetail.contractType}" aus.`
          : `Bitte füllen Sie die Strom-Vertragsdaten für "${missingRequiredDetail.contractType}" aus.`
      );
      return;
    }

    setShowConfirmation(true);
  };

  const handleSaveContract = async () => {
    if (!tempAddress?.id || contractData.selectedContractTypes.length === 0) {
      setSaveError(
        contractData.selectedContractTypes.length === 0
          ? "Bitte wählen Sie mindestens einen Produkt aus"
          : "Adress-ID fehlt"
      );
      return;
    }

    const invalidContractType = getInvalidSelectedContractType();
    if (invalidContractType) {
      setSaveError(
        "Ein ausgewähltes Produkt ist unvollständig. Bitte entfernen Sie es und wählen Sie es erneut aus."
      );
      return;
    }

    const birthDateResult = getValidatedBirthDateForSave();
    if (birthDateResult.error) {
      setBirthDateValidationError(birthDateResult.error);
      setSaveError(birthDateResult.error);
      return;
    }

    if (birthDateValidationError) {
      setSaveError(birthDateValidationError);
      return;
    }

    const missingRequiredDetail = getMissingRequiredUtilityDetail(
      contractData.selectedContractTypes
    );
    if (missingRequiredDetail) {
      setSaveError(
        missingRequiredDetail.detailType === "gas"
          ? `Bitte füllen Sie die Gas-Vertragsdaten für "${missingRequiredDetail.contractType}" aus.`
          : `Bitte füllen Sie die Strom-Vertragsdaten für "${missingRequiredDetail.contractType}" aus.`
      );
      return;
    }

    // Check for date validation errors
    if (electricityDateError || gasDateError) {
      setSaveError(
        "Bitte korrigieren Sie die Fehler bei den Lieferbeginn-Daten"
      );
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    Sentry.addBreadcrumb({
      category: "contract.save",
      level: "info",
      message: "contract.save.start",
      data: {
        route: "/contract-creation",
        selectedContractTypeCount: contractData.selectedContractTypes.length,
      },
    });

    // Standardize floor value
    let processedFloor = contractData.floor;
    if (processedFloor === "EG") {
      processedFloor = "0";
    } else if (processedFloor.endsWith(".")) {
      processedFloor = processedFloor.replace(".", "");
    }

    try {
      let pdfGenerationFailed = false;
      let pdfGenerationError: {
        code?: string;
        message: string;
        details?: string;
      } | null = null;

      // Get the first contract type to create the customer
      const firstContractType = contractData.selectedContractTypes[0];

      if (!firstContractType) {
        throw new Error("Kein Produkt ausgewählt");
      }

      const formattedBirthDate = birthDateResult.birthdate;

      if (!formattedBirthDate) {
        throw new Error("Ungültiges Geburtsdatum");
      }

      let result: {
        customer: { id: number };
        contract: { id: number };
        success: boolean;
      };
      let customerIdForMetadata: number | null = null;
      let createdContractRefs: {
        contractTypeId: number;
        contractType: string;
        contractId: number;
      }[] = [];

      // Check if we already have saved data from a previous attempt
      if (savedCustomerData) {
        console.log("Using previously saved customer data:", savedCustomerData);
        result = { ...savedCustomerData, success: true };
        customerIdForMetadata = savedCustomerData.customer.id;
        if (savedContractRefs) {
          createdContractRefs = savedContractRefs;
        }
      } else {
        // Create customer and first contract
        console.log(
          "Creating first contract with contract type:",
          firstContractType.id
        );
        result = await saveContract({
          firstName: contractData.contract.firstName,
          lastName: contractData.name,
          birthdate: formattedBirthDate,
          email: contractData.contract.email,
          phone: contractData.contract.phone,
          bank_details: contractData.contract.bank_details,
          floor: processedFloor,
          notes: contractData.comments,
          address_id: tempAddress.id,
          contract_type_id: firstContractType.id,
          signature_path: contractData.contract.signature_path,
          gender: contractData.gender,
        });

        console.log("First contract saved, full result:", result);

        // Save utility details if applicable for first contract
        if (result.contract && result.contract.id) {
          const contractId = result.contract.id;
          const requiredDetailType = getRequiredUtilityType(
            firstContractType.type,
            firstContractType.category
          );

          if (requiredDetailType === "electricity") {
            console.log(
              "Saving electricity details for first contract:",
              contractId
            );
            await saveElectricityContractDetails({
              contract_id: contractId,
              previous_provider_name:
                electricityDetails.previous_provider_name.trim(),
              previous_customer_number:
                electricityDetails.previous_customer_number.trim(),
              annual_consumption_kwh: convertToNumber(
                electricityDetails.annual_consumption_kwh
              ),
              meter_point_id: convertToNullableString(
                electricityDetails.meter_point_id
              ),
              meter_number: electricityDetails.meter_number.trim(),
              meter_reading: convertToNullableNumber(
                electricityDetails.meter_reading
              ),
              unit_price_ct_kwh: convertToNumber(
                electricityDetails.unit_price_ct_kwh
              ),
              base_price_eur_month: convertToNumber(
                electricityDetails.base_price_eur_month
              ),
              delivery_start_date: electricityDetails.as_soon_as_possible
                ? null
                : electricityDetails.delivery_start_date,
              as_soon_as_possible: electricityDetails.as_soon_as_possible,
              desired_monthly_payment: convertToNullableNumber(
                electricityDetails.desired_monthly_payment
              ),
            });
          }

          if (requiredDetailType === "gas") {
            console.log("Saving gas details for first contract:", contractId);
            await saveGasContractDetails({
              contract_id: contractId,
              previous_provider_name: gasDetails.previous_provider_name.trim(),
              previous_customer_number:
                gasDetails.previous_customer_number.trim(),
              annual_consumption_kwh: convertToNumber(
                gasDetails.annual_consumption_kwh
              ),
              meter_point_id: convertToNullableString(
                gasDetails.meter_point_id
              ),
              meter_number: gasDetails.meter_number.trim(),
              meter_reading: convertToNullableNumber(gasDetails.meter_reading),
              unit_price_ct_kwh: convertToNumber(gasDetails.unit_price_ct_kwh),
              base_price_eur_month: convertToNumber(
                gasDetails.base_price_eur_month
              ),
              delivery_start_date: gasDetails.as_soon_as_possible
                ? null
                : gasDetails.delivery_start_date,
              as_soon_as_possible: gasDetails.as_soon_as_possible,
              desired_monthly_payment: convertToNullableNumber(
                gasDetails.desired_monthly_payment
              ),
            });
          }
        }

        // Safely extract customer ID, ensuring it exists
        const customerIdFromResult = result.customer?.id;
        customerIdForMetadata = customerIdFromResult ?? null;
        console.log("Customer ID from first contract:", customerIdFromResult);

        if (!customerIdFromResult) {
          console.error(
            "ERROR: No customer ID returned from first contract creation!"
          );
          setSaveError(
            "Fehler: Kunde konnte nicht erstellt werden. Bitte versuchen Sie es erneut."
          );
          return;
        }

        // If there are more contract types, create additional contracts for the same customer
        if (
          contractData.selectedContractTypes.length > 1 &&
          customerIdFromResult
        ) {
          console.log(
            `Creating ${
              contractData.selectedContractTypes.length - 1
            } additional contracts for customer ID:`,
            customerIdFromResult
          );

          // Create additional contracts for the same customer
          for (let i = 1; i < contractData.selectedContractTypes.length; i++) {
            const contractType = contractData.selectedContractTypes[i];
            if (contractType) {
              console.log(
                `Creating contract #${i + 1} of type:`,
                contractType.id,
                "for customer:",
                customerIdFromResult
              );

              const additionalResult = await saveContract({
                customer_id: customerIdFromResult, // Use existing customer
                firstName: contractData.contract.firstName,
                lastName: contractData.name,
                birthdate: formattedBirthDate,
                email: contractData.contract.email,
                phone: contractData.contract.phone,
                bank_details: contractData.contract.bank_details,
                floor: processedFloor,
                notes: contractData.comments,
                address_id: tempAddress.id,
                contract_type_id: contractType.id,
                signature_path: contractData.contract.signature_path,
                gender: contractData.gender,
              });

              console.log(
                `Contract #${i + 1} saved successfully, result:`,
                additionalResult
              );

              // Save utility details if applicable for additional contract
              if (additionalResult.contract && additionalResult.contract.id) {
                const contractId = additionalResult.contract.id;
                createdContractRefs.push({
                  contractTypeId: contractType.id,
                  contractType: contractType.type,
                  contractId,
                });
                const requiredDetailType = getRequiredUtilityType(
                  contractType.type,
                  contractType.category
                );

                if (requiredDetailType === "electricity") {
                  console.log(
                    `Saving electricity details for contract #${i + 1}:`,
                    contractId
                  );
                  await saveElectricityContractDetails({
                    contract_id: contractId,
                    previous_provider_name:
                      electricityDetails.previous_provider_name.trim(),
                    previous_customer_number:
                      electricityDetails.previous_customer_number.trim(),
                    annual_consumption_kwh: convertToNumber(
                      electricityDetails.annual_consumption_kwh
                    ),
                    meter_point_id: convertToNullableString(
                      electricityDetails.meter_point_id
                    ),
                    meter_number: electricityDetails.meter_number.trim(),
                    meter_reading: convertToNullableNumber(
                      electricityDetails.meter_reading
                    ),
                    unit_price_ct_kwh: convertToNumber(
                      electricityDetails.unit_price_ct_kwh
                    ),
                    base_price_eur_month: convertToNumber(
                      electricityDetails.base_price_eur_month
                    ),
                    delivery_start_date: electricityDetails.as_soon_as_possible
                      ? null
                      : electricityDetails.delivery_start_date,
                    as_soon_as_possible: electricityDetails.as_soon_as_possible,
                    desired_monthly_payment: convertToNullableNumber(
                      electricityDetails.desired_monthly_payment
                    ),
                  });
                }

                if (requiredDetailType === "gas") {
                  console.log(
                    `Saving gas details for contract #${i + 1}:`,
                    contractId
                  );
                  await saveGasContractDetails({
                    contract_id: contractId,
                    previous_provider_name:
                      gasDetails.previous_provider_name.trim(),
                    previous_customer_number:
                      gasDetails.previous_customer_number.trim(),
                    annual_consumption_kwh: convertToNumber(
                      gasDetails.annual_consumption_kwh
                    ),
                    meter_point_id: convertToNullableString(
                      gasDetails.meter_point_id
                    ),
                    meter_number: gasDetails.meter_number.trim(),
                    meter_reading: convertToNullableNumber(
                      gasDetails.meter_reading
                    ),
                    unit_price_ct_kwh: convertToNumber(
                      gasDetails.unit_price_ct_kwh
                    ),
                    base_price_eur_month: convertToNumber(
                      gasDetails.base_price_eur_month
                    ),
                    delivery_start_date: gasDetails.as_soon_as_possible
                      ? null
                      : gasDetails.delivery_start_date,
                    as_soon_as_possible: gasDetails.as_soon_as_possible,
                    desired_monthly_payment: convertToNullableNumber(
                      gasDetails.desired_monthly_payment
                    ),
                  });
                }
              }
            }
          }
        }

        if (result.contract?.id) {
          createdContractRefs.unshift({
            contractTypeId: firstContractType.id,
            contractType: firstContractType.type,
            contractId: result.contract.id,
          });
        }

        // Cache the successful result
        setSavedCustomerData(result);
        setSavedContractRefs(createdContractRefs);
      }

      console.log("All contracts saved successfully");
      Sentry.addBreadcrumb({
        category: "contract.save",
        level: "info",
        message: "contract.save.success",
        data: {
          route: "/contract-creation",
          customerId: customerIdForMetadata,
          contractCount: createdContractRefs.length,
        },
      });

      // Show success notification for customer saved
      showNotification("Kunde gespeichert", "success");

      // Check if PDF generation and email sending is required
      // Only for Stadtwerke Dessau category with Gas or Strom contracts
      const hasStadtwerkeDessauContract =
        contractData.selectedContractTypes.some((ct) =>
          isStadtwerkeDessauCategory(ct.category)
        );
      const hasGasOrStromContract = contractData.selectedContractTypes.some(
        (ct) =>
          isGasContract(ct.type) || ct.type.toLowerCase().includes("strom")
      );

      const shouldGeneratePDF =
        hasStadtwerkeDessauContract && hasGasOrStromContract;

      console.log(
        "Has Stadtwerke Dessau contract:",
        hasStadtwerkeDessauContract
      );
      console.log("Has Gas/Strom contract:", hasGasOrStromContract);
      console.log("Should generate PDF:", shouldGeneratePDF);

      // Generate PDFs for each contract type that needs it
      if (shouldGeneratePDF) {
        console.log("Starting PDF generation and email flow...");
        Sentry.addBreadcrumb({
          category: "contract.pdf",
          level: "info",
          message: "contract.pdf.start",
          data: {
            route: "/contract-creation",
            contractCount: contractData.selectedContractTypes.length,
          },
        });

        // Ensure worker data (signature) is loaded before generating PDF
        // This fixes the race condition where signature might be missing
        let currentWorkerData = workerData;
        if (user?.id) {
          console.log(
            "Ensuring worker data is loaded before PDF generation..."
          );
          const freshWorkerData = await getWorkerData(user.id, true);
          if (freshWorkerData) {
            currentWorkerData = freshWorkerData;
            setWorkerData(freshWorkerData); // Update state as well
            console.log("Worker data refreshed/loaded for PDF generation");
          }
        }

        // Use cached customer signature or download from storage for PDF generation
        let customerSignatureBase64: string | undefined;
        if (customerSignatureDataUrl) {
          // Use cached signature (from current session)
          customerSignatureBase64 = customerSignatureDataUrl;
          console.log("Using cached customer signature for PDF");
        } else if (contractData.contract.signature_path) {
          // Fallback: download from storage if not cached (e.g., after page refresh)
          try {
            customerSignatureBase64 = await downloadSignatureAsBase64(
              contractData.contract.signature_path
            );
            console.log(
              "Customer signature downloaded from storage (cache miss)"
            );
          } catch (error) {
            console.error("Error downloading customer signature:", error);
            // Continue without signature rather than failing entirely
          }
        }

        // Filter only Gas/Strom contracts for PDF generation
        const gasStromContracts = contractData.selectedContractTypes.filter(
          (ct) =>
            isGasContract(ct.type) || ct.type.toLowerCase().includes("strom")
        );

        // Step 1: Generate all PDFs first
        let workerIdForMetadata: number | undefined;
        if (user?.id) {
          try {
            workerIdForMetadata = await getWorkerIdForUser(user.id);
          } catch (workerIdError) {
            console.warn("Could not resolve worker id for email metadata", {
              error: workerIdError,
            });
          }
        }
        const generatedPdfs: {
          blob: Blob;
          contractType: string;
          metadata: {
            source: "contract_api";
            contractId?: number;
            customerId?: number;
            contractType: string;
            createdByWorkerId?: number;
            createdByUserId?: string;
          };
        }[] = [];

        for (const contractType of gasStromContracts) {
          try {
            console.log(
              `Generating PDF for contract type: ${contractType.type}`
            );

            const { templatePath } = determinePdfTemplate(contractType.type);
            console.log("PDF template selected:", templatePath);

            // Debug: Log employee signature status
            console.log("🔍 Employee signature status:", {
              hasWorkerData: !!currentWorkerData,
              hasSignature: !!currentWorkerData?.signature_base64,
              signatureLength: currentWorkerData?.signature_base64?.length,
              signaturePreview:
                currentWorkerData?.signature_base64?.substring(0, 50) + "...",
            });

            // Prepare PDF data for this specific contract type
            const pdfData: ContractPdfData = {
              customer: {
                firstName: contractData.contract.firstName,
                lastName: contractData.name,
                birthdate: useDateTextInput
                  ? birthDateText
                  : formatBirthDateForDisplay(contractData.contract.birthDate),
                email: contractData.contract.email,
                phone: contractData.contract.phone,
                address: {
                  street: tempAddress.street || "",
                  houseNumber: tempAddress.house_number || "",
                  zipCode: tempAddress.zip_code || "",
                  city: tempAddress.city || "",
                  country: tempAddress.country || "",
                },
              },
              gender: contractData.gender,
              bankDetails: contractData.contract.bank_details,
              signatureBase64: customerSignatureBase64,
              contractType: contractType.type,
              selectedDate: getCurrentDateFormatted(),
              // Optional general fields
              salesPartnerCompany: currentWorkerData?.organization || undefined,
              salesEmployeeName: currentWorkerData?.full_name || undefined,
              employeeSignatureBase64:
                currentWorkerData?.signature_base64 || undefined,
              invoiceRecipient: undefined, // TODO: Add form field if needed
              invoiceStreet: undefined, // TODO: Add form field if needed
              invoiceZipCity: undefined, // TODO: Add form field if needed
              signatureLocation: tempAddress.city || undefined,
              // Utility-specific details
              gasDetails: isGasContract(contractType.type)
                ? {
                    previous_provider_name: gasDetails.previous_provider_name,
                    previous_customer_number:
                      gasDetails.previous_customer_number,
                    annual_consumption_kwh: gasDetails.annual_consumption_kwh,
                    meter_point_id: gasDetails.meter_point_id || undefined,
                    meter_number: gasDetails.meter_number,
                    meter_reading: gasDetails.meter_reading || undefined,
                    unit_price_ct_kwh: gasDetails.unit_price_ct_kwh,
                    base_price_eur_month: gasDetails.base_price_eur_month,
                    delivery_start_date: gasDetails.as_soon_as_possible
                      ? "schellst."
                      : gasDetails.delivery_start_date,
                    as_soon_as_possible: gasDetails.as_soon_as_possible,
                    desired_monthly_payment:
                      gasDetails.desired_monthly_payment || undefined,
                  }
                : undefined,
              electricityDetails: contractType.type
                .toLowerCase()
                .includes("strom")
                ? {
                    previous_provider_name:
                      electricityDetails.previous_provider_name,
                    previous_customer_number:
                      electricityDetails.previous_customer_number,
                    annual_consumption_kwh:
                      electricityDetails.annual_consumption_kwh,
                    meter_point_id:
                      electricityDetails.meter_point_id || undefined,
                    meter_number: electricityDetails.meter_number,
                    meter_reading:
                      electricityDetails.meter_reading || undefined,
                    unit_price_ct_kwh: electricityDetails.unit_price_ct_kwh,
                    base_price_eur_month:
                      electricityDetails.base_price_eur_month,
                    delivery_start_date: electricityDetails.as_soon_as_possible
                      ? "schellst."
                      : electricityDetails.delivery_start_date,
                    as_soon_as_possible: electricityDetails.as_soon_as_possible,
                    desired_monthly_payment:
                      electricityDetails.desired_monthly_payment || undefined,
                  }
                : undefined,
            };

            // Generate PDF
            console.log(
              `Generating PDF for ${contractType.type} with data:`,
              pdfData
            );
            const pdfResult = await generateContractPdf(pdfData, templatePath);
            console.log(
              `PDF generation result for ${contractType.type}:`,
              pdfResult
            );

            if (!pdfResult.success || !pdfResult.pdfBlob) {
              pdfGenerationFailed = true;
              pdfGenerationError = {
                code: pdfResult.errorCode,
                message: pdfResult.error || "PDF generation failed",
                details: pdfResult.errorDetails,
              };
              console.error(
                `PDF generation blocked for ${contractType.type}:`,
                pdfGenerationError
              );
              break;
            }

            console.log(
              `✓ PDF generated successfully for ${contractType.type}`
            );
            const matchedContractRef = createdContractRefs.find(
              (item) => item.contractTypeId === contractType.id
            );
            generatedPdfs.push({
              blob: pdfResult.pdfBlob,
              contractType: contractType.type,
              metadata: {
                source: "contract_api",
                contractId: matchedContractRef?.contractId,
                customerId: customerIdForMetadata ?? undefined,
                contractType: contractType.type,
                createdByWorkerId: workerIdForMetadata,
                createdByUserId: user?.id,
              },
            });
          } catch (pdfError) {
            console.error(
              `PDF generation error for ${contractType.type}:`,
              pdfError
            );
            pdfGenerationFailed = true;
            pdfGenerationError = {
              message:
                pdfError instanceof Error
                  ? pdfError.message
                  : "PDF generation failed",
            };
            break; // Stop generating more PDFs if one fails
          }
        }

        // Step 2: Send one email per contract type (management jobs first, then customer jobs)
        if (!pdfGenerationFailed && generatedPdfs.length > 0) {
          console.log(`Sending ${generatedPdfs.length} PDF(s) via email...`);
          Sentry.addBreadcrumb({
            category: "contract.email",
            level: "info",
            message: "contract.email.start",
            data: {
              route: "/contract-creation",
              pdfCount: generatedPdfs.length,
              hasCustomerEmail: Boolean(contractData.contract.email),
            },
          });

          const emailResult =
            await sendContractEmailBatchWithManagementPriority(
              generatedPdfs,
              contractData.contract.email,
              `${contractData.contract.firstName} ${contractData.name}`,
              contractData.name
            );

          if (emailResult.managementEmailSent) {
            showNotification(
              "Mail an verwaltung@teleconnect24.de gesendet",
              "success"
            );
          }
          if (
            !emailResult.customerEmailMissing &&
            emailResult.customerEmailSent
          ) {
            showNotification(
              `Mail an ${contractData.contract.email} gesendet`,
              "success"
            );
          }

          // Handle email result
          let summaryMessage = "";
          const managementSentCount =
            emailResult.managementSentCount ??
            (emailResult.managementEmailSent ? generatedPdfs.length : 0);
          const customerTargetCount = emailResult.customerEmailMissing
            ? 0
            : generatedPdfs.length;
          const customerSentCount =
            emailResult.customerSentCount ??
            (emailResult.customerEmailSent ? customerTargetCount : 0);
          const failedJobsCount = emailResult.failedJobs?.length ?? 0;

          if (emailResult.success) {
            const contractTypes = generatedPdfs
              .map((p) => p.contractType)
              .join(" und ");
            summaryMessage =
              generatedPdfs.length === 1
                ? `Vertrag (${contractTypes}) erfolgreich gespeichert und per E-Mail versendet!`
                : `Alle ${generatedPdfs.length} Verträge (${contractTypes}) erfolgreich gespeichert und per E-Mail versendet!`;
            setSuccessModalSuccess(true);
            setSuccessModalActionLabel(undefined);
          } else if (emailResult.customerEmailMissing) {
            summaryMessage = `Verträge gespeichert. Backoffice-E-Mails: ${managementSentCount}/${generatedPdfs.length}. Kunde hat keine E-Mail-Adresse, PDFs wurden lokal gespeichert.`;
            setSuccessModalSuccess(
              managementSentCount === generatedPdfs.length
            );
            setSuccessModalActionLabel(undefined);
          } else if (emailResult.downloadTriggered) {
            summaryMessage = `Verträge gespeichert. Backoffice-E-Mails: ${managementSentCount}/${generatedPdfs.length}, Kunden-E-Mails: ${customerSentCount}/${customerTargetCount}. Fehlgeschlagene Sendungen: ${failedJobsCount}. PDFs wurden lokal gespeichert.`;
            setSuccessModalSuccess(false);
            setSuccessModalActionLabel(undefined);
          } else {
            summaryMessage = `Verträge gespeichert. Backoffice-E-Mails: ${managementSentCount}/${generatedPdfs.length}, Kunden-E-Mails: ${customerSentCount}/${customerTargetCount}. Fehlgeschlagene Sendungen: ${failedJobsCount}.`;
            setSuccessModalSuccess(false);
            setSuccessModalActionLabel(undefined);
          }
          setSuccessModalMessage(summaryMessage);
        } else {
          const isPdfIntegrityFailure =
            pdfGenerationError?.code === "PDF_TEMPLATE_INTEGRITY_FAILED" ||
            pdfGenerationError?.code === "PDF_FILLED_OUTPUT_INVALID" ||
            pdfGenerationError?.code === "PDF_FIELD_APPEARANCE_FAILED" ||
            pdfGenerationError?.code === "PDF_FLATTEN_FAILED";

          const summaryMessage = isPdfIntegrityFailure
            ? `Verträge wurden gespeichert, aber der E-Mail-Versand wurde wegen eines PDF-Integritätsfehlers blockiert (${
                pdfGenerationError?.code ?? "UNKNOWN"
              }). Bitte informieren Sie die Verwaltung, damit der Versand manuell geprüft und ausgelöst wird.`
            : "Verträge gespeichert, aber PDF-Generierung fehlgeschlagen.";
          setSuccessModalSuccess(false);
          setSuccessModalMessage(summaryMessage);
          setSuccessModalActionLabel("Als gespeichert ohne E-Mail schließen");
        }

        setShowSuccessModal(true);
        setSaveSuccess(false); // Don't show standard success, use modal instead
      } else {
        // No PDF needed, show standard success
        console.log(
          "PDF generation skipped - category does not match energy categories"
        );
        setSaveSuccess(true);
      }

      setShowConfirmation(false);

      // Reset form after successful save ONLY if no errors occurred
      if (!pdfGenerationFailed) {
        resetForm();
      }
    } catch (error) {
      console.error("Error in handleSaveContract:", error);
      Sentry.captureException(error, {
        tags: {
          operation: "contract.save",
          route: "/contract-creation",
        },
        extra: {
          selectedContractTypeCount: contractData.selectedContractTypes.length,
          hasAddressId: Boolean(tempAddress?.id),
        },
      });
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Fehler beim Speichern des Kunden";
      const isUtilityDetailsError =
        /electricity details|gas details|utility details|strom|gas/i.test(
          errorMessage
        );
      setSaveError(
        isUtilityDetailsError
          ? `Vertrag wurde eventuell teilweise gespeichert, aber Energiedaten konnten nicht vollständig gespeichert werden: ${errorMessage}`
          : errorMessage
      );
    } finally {
      console.log("handleSaveContract finished, setIsSaving(false)");
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    // Reset form but keep address
    setShowSignaturePad(false);
    setCustomerSignatureDataUrl(null);
    setSaveSuccess(false);
    setSaveError(null);
    setSearchQuery(""); // Reset search query
    setCurrentPage(1); // Reset pagination
    setBirthDateText(""); // Reset birth date text input
    setUseDateTextInput(false); // Reset to date picker
    setBirthDateValidationError(null); // Reset validation error
    setSavedCustomerData(null); // Reset saved customer data
    setSavedContractRefs(null); // Reset saved contract references
    updateContractData({
      ...contractData,
      name: "",
      floor: "",
      customerState: "",
      comments: "",
      contract: {
        firstName: "",
        birthDate: "",
        customerNumber: "",
        email: "",
        phone: "",
        category: "",
        company: "",
        bank_details: "",
        signature_path: "",
      },
      utilityDetails: {
        electricity: createEmptyUtilityContractDetails(),
        gas: createEmptyUtilityContractDetails(),
      },
      selectedContractTypes: [],
    });
    clearContractCardDraft();
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  return (
    <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
      <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
        Details
      </h3>
      <div className="space-y-4">
        {/* Success Message */}
        {saveSuccess && (
          <Alert color="success" onDismiss={() => setSaveSuccess(false)}>
            Kunde und Produkt erfolgreich gespeichert
          </Alert>
        )}

        {/* Gender Selection */}
        <div>
          <label
            htmlFor="gender"
            className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
          >
            Anrede
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => updateContractData({ gender: 1 })}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                contractData.gender === 1
                  ? "bg-primary-700 text-white"
                  : "bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
              }`}
            >
              Herr
            </button>
            <button
              type="button"
              onClick={() => updateContractData({ gender: 2 })}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                contractData.gender === 2
                  ? "bg-primary-700 text-white"
                  : "bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
              }`}
            >
              Frau
            </button>
            <button
              type="button"
              onClick={() => updateContractData({ gender: 3 })}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                contractData.gender === 3
                  ? "bg-primary-700 text-white"
                  : "bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
              }`}
            >
              Divers
            </button>
          </div>
        </div>

        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="firstName"
              className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
            >
              Vorname
            </label>
            <input
              type="text"
              id="firstName"
              value={contractData.contract.firstName}
              onChange={(e) => handleInputChange(e, "firstName")}
              className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="lastName"
              className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
            >
              Nachname
            </label>
            <input
              type="text"
              id="lastName"
              value={contractData.name}
              disabled
              className="block w-full rounded-lg border border-gray-300 bg-gray-100 p-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-600 dark:text-gray-400"
            />
          </div>
        </div>

        {/* Birth Date */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <label
              htmlFor="birthDate"
              className="block text-sm font-medium text-gray-900 dark:text-white"
            >
              Geburtsdatum
            </label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Datumseingabe
              </span>
              <ToggleSwitch
                checked={useDateTextInput}
                onChange={handleToggleChange}
                label=""
              />
            </div>
          </div>

          {useDateTextInput ? (
            <TextInput
              id="birthdate_text"
              type="text"
              value={birthDateText}
              onChange={(e) => handleBirthDateTextChange(e.target.value)}
              className="block w-full"
              placeholder="TT.MM.JJJJ"
            />
          ) : (
            <input
              type="date"
              id="birthDate"
              value={contractData.contract.birthDate || defaultBirthDate}
              max={defaultBirthDate}
              onChange={(e) => {
                handleInputChange(e, "birthDate");

                // Validate age when date is selected
                if (e.target.value) {
                  const validation = validateBirthDateAge(e.target.value);
                  if (!validation.isValid) {
                    setBirthDateValidationError(validation.message);
                  } else {
                    setBirthDateValidationError(null);
                  }
                } else {
                  setBirthDateValidationError(null);
                }
              }}
              className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
            />
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Format: TT.MM.JJJJ
          </p>
          {/* Birth Date Validation Error */}
          {birthDateValidationError && (
            <div className="mt-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {birthDateValidationError}
            </div>
          )}
        </div>

        {/* Customer Number */}
        <div>
          <label
            htmlFor="customerNumber"
            className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
          >
            Kundennummer
          </label>
          <input
            type="text"
            id="customerNumber"
            value={contractData.contract.customerNumber}
            onChange={(e) => handleInputChange(e, "customerNumber")}
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
          />
        </div>

        {/* Email */}
        <div>
          <label
            htmlFor="email"
            className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
          >
            E-Mail
          </label>
          <input
            type="email"
            id="email"
            value={contractData.contract.email}
            onChange={(e) => handleInputChange(e, "email")}
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
          />
        </div>

        {/* Phone */}
        <div>
          <label
            htmlFor="phone"
            className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
          >
            Telefonnummer
          </label>
          <input
            type="tel"
            id="phone"
            value={contractData.contract.phone}
            onChange={(e) => handleInputChange(e, "phone")}
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
          />
        </div>

        {/* Bank Details */}
        <div>
          <label
            htmlFor="bank_details"
            className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
          >
            Bankverbindung
          </label>
          <input
            type="text"
            id="bank_details"
            value={
              bankDetailsFocused
                ? contractData.contract.bank_details
                : (contractData.contract.bank_details || "").replace(/./g, "•")
            }
            onChange={(e) => handleInputChange(e, "bank_details")}
            onFocus={() => setBankDetailsFocused(true)}
            onBlur={() => setBankDetailsFocused(false)}
            autoComplete="off"
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
            placeholder="IBAN oder Kontonummer"
          />
        </div>

        {/* Contract Category and Company */}
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <MultiSelect
              id="category"
              label="Kategorie"
              options={categoryOptions}
              value={selectedCategories}
              onChange={handleSelectedCategoriesChange}
              placeholder={
                isLoading ? "Kategorien werden geladen..." : "Alle Kategorien"
              }
              searchPlaceholder="Kategorie suchen..."
            />

            <MultiSelect
              id="company"
              label="Unternehmen"
              options={companyOptions}
              value={selectedCompanies}
              onChange={handleSelectedCompaniesChange}
              placeholder={
                isLoading ? "Unternehmen werden geladen..." : "Alle Unternehmen"
              }
              searchPlaceholder="Unternehmen suchen..."
            />
          </div>
          {(hasActiveProductFilters || error) && (
            <div className="flex flex-wrap items-center gap-3">
              {hasActiveProductFilters && (
                <Button color="light" size="sm" onClick={handleClearFilters}>
                  Filter zurücksetzen
                </Button>
              )}
              {error && (
                <p className="text-sm text-red-600 dark:text-red-500">
                  {error}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Helper function to check if electricity or gas fields should be shown */}
        {(() => {
          const hasStromInCategory = selectedCategories.some((category) =>
            category.toLowerCase().includes("strom")
          );
          const hasGasInCategory = selectedCategories.some((category) =>
            category.toLowerCase().includes("gas")
          );
          const selectedTypes = contractData.selectedContractTypes || [];
          const hasStromInTypes = selectedTypes.some((ct) =>
            ct.type.toLowerCase().includes("strom")
          );
          const hasGasInTypes = selectedTypes.some((ct) =>
            isGasContract(ct.type)
          );

          const showElectricity = hasStromInCategory || hasStromInTypes;
          const showGas = hasGasInCategory || hasGasInTypes;

          return (
            <>
              {/* Electricity (Strom) Details */}
              {showElectricity && (
                <div className="mt-6 space-y-4 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                    Stromvertragsdetails
                  </h4>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="elec_prev_provider"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Name des bisherigen Stromlieferanten *
                      </label>
                      <TextInput
                        id="elec_prev_provider"
                        type="text"
                        value={electricityDetails.previous_provider_name}
                        onChange={(e) =>
                          setElectricityDetails({
                            ...electricityDetails,
                            previous_provider_name: e.target.value,
                          })
                        }
                        required
                        placeholder="z.B. Vattenfall"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="elec_prev_customer_num"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Kundennummer beim bisherigen Stromlieferanten *
                      </label>
                      <TextInput
                        id="elec_prev_customer_num"
                        type="text"
                        value={electricityDetails.previous_customer_number}
                        onChange={(e) =>
                          setElectricityDetails({
                            ...electricityDetails,
                            previous_customer_number: e.target.value,
                          })
                        }
                        required
                        placeholder="Kundennummer"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="elec_annual_consumption"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Vorjahresstromverbrauch in kWh *
                      </label>
                      <TextInput
                        id="elec_annual_consumption"
                        type="number"
                        value={electricityDetails.annual_consumption_kwh}
                        onChange={(e) =>
                          setElectricityDetails({
                            ...electricityDetails,
                            annual_consumption_kwh: e.target.value,
                          })
                        }
                        required
                        placeholder="z.B. 3500"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="elec_meter_point_id"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Zählpunktbezeichnung / ID der Marktlokation
                      </label>
                      <TextInput
                        id="elec_meter_point_id"
                        type="text"
                        value={electricityDetails.meter_point_id}
                        onChange={(e) =>
                          setElectricityDetails({
                            ...electricityDetails,
                            meter_point_id: e.target.value,
                          })
                        }
                        placeholder="Optional"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="elec_meter_number"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Eigentumsnummer des Stromzählers *
                      </label>
                      <TextInput
                        id="elec_meter_number"
                        type="text"
                        value={electricityDetails.meter_number}
                        onChange={(e) =>
                          setElectricityDetails({
                            ...electricityDetails,
                            meter_number: e.target.value,
                          })
                        }
                        required
                        placeholder="Zählernummer"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="elec_meter_reading"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Zählerstand
                      </label>
                      <TextInput
                        id="elec_meter_reading"
                        type="number"
                        value={electricityDetails.meter_reading}
                        onChange={(e) =>
                          setElectricityDetails({
                            ...electricityDetails,
                            meter_reading: e.target.value,
                          })
                        }
                        placeholder="Optional"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="elec_unit_price"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Arbeitspreis ct/kWh/brutto *
                      </label>
                      <TextInput
                        id="elec_unit_price"
                        type="number"
                        step="0.01"
                        value={electricityDetails.unit_price_ct_kwh}
                        onChange={(e) =>
                          setElectricityDetails({
                            ...electricityDetails,
                            unit_price_ct_kwh: e.target.value,
                          })
                        }
                        required
                        placeholder="z.B. 32.50"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="elec_base_price"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Grundpreis EUR/Monat/brutto *
                      </label>
                      <TextInput
                        id="elec_base_price"
                        type="number"
                        step="0.01"
                        value={electricityDetails.base_price_eur_month}
                        onChange={(e) =>
                          setElectricityDetails({
                            ...electricityDetails,
                            base_price_eur_month: e.target.value,
                          })
                        }
                        required
                        placeholder="z.B. 12.99"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="elec_delivery_start"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Nächstmöglicher Zeitpunkt Lieferbeginn *
                      </label>

                      {/* Checkbox for "as soon as possible" */}
                      <div className="mb-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={electricityDetails.as_soon_as_possible}
                            onChange={(e) => {
                              setElectricityDetails({
                                ...electricityDetails,
                                as_soon_as_possible: e.target.checked,
                                delivery_start_date: e.target.checked
                                  ? ""
                                  : electricityDetails.delivery_start_date,
                              });
                              // Clear date error when checkbox is checked
                              if (e.target.checked) {
                                setElectricityDateError(null);
                              }
                            }}
                            className="mr-2 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Schnellstmöglich (kein spezifisches Datum)
                          </span>
                        </label>
                      </div>

                      {/* Date input field */}
                      <input
                        id="elec_delivery_start"
                        type="date"
                        value={electricityDetails.delivery_start_date}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          setElectricityDetails({
                            ...electricityDetails,
                            delivery_start_date: newDate,
                          });

                          // Validate date
                          if (newDate && !validateDeliveryDate(newDate)) {
                            setElectricityDateError(
                              "Das Lieferbeginn-Datum muss nach dem heutigen Datum liegen"
                            );
                          } else {
                            setElectricityDateError(null);
                          }
                        }}
                        required={!electricityDetails.as_soon_as_possible}
                        disabled={electricityDetails.as_soon_as_possible}
                        className={`block w-full rounded-lg border p-2.5 text-sm ${
                          electricityDetails.as_soon_as_possible
                            ? "cursor-not-allowed bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500"
                            : electricityDateError
                            ? "border-red-500 bg-red-50 text-red-900 focus:border-red-500 focus:ring-red-500 dark:border-red-400 dark:bg-red-900/20 dark:text-red-400"
                            : "border-gray-300 bg-gray-50 text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        } dark:placeholder:text-gray-400`}
                      />
                      {electricityDateError && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          {electricityDateError}
                        </p>
                      )}

                      {/* Show "schellst." indicator when checkbox is checked */}
                      {electricityDetails.as_soon_as_possible && (
                        <div className="mt-2 rounded-md bg-blue-50 p-2 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                          ✓ Lieferbeginn: Schnellstmöglich
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="elec_monthly_payment"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        gewünschter monatl. Abschlag
                      </label>
                      <TextInput
                        id="elec_monthly_payment"
                        type="number"
                        step="0.01"
                        value={electricityDetails.desired_monthly_payment}
                        onChange={(e) =>
                          setElectricityDetails({
                            ...electricityDetails,
                            desired_monthly_payment: e.target.value,
                          })
                        }
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Gas Details */}
              {showGas && (
                <div className="mt-6 space-y-4 rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-900/20">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                    Gasvertragsdetails
                  </h4>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="gas_prev_provider"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Name des bisherigen Gaslieferanten *
                      </label>
                      <TextInput
                        id="gas_prev_provider"
                        type="text"
                        value={gasDetails.previous_provider_name}
                        onChange={(e) =>
                          setGasDetails({
                            ...gasDetails,
                            previous_provider_name: e.target.value,
                          })
                        }
                        required
                        placeholder="z.B. Gasag"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="gas_prev_customer_num"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Kundennummer beim bisherigen Gaslieferanten *
                      </label>
                      <TextInput
                        id="gas_prev_customer_num"
                        type="text"
                        value={gasDetails.previous_customer_number}
                        onChange={(e) =>
                          setGasDetails({
                            ...gasDetails,
                            previous_customer_number: e.target.value,
                          })
                        }
                        required
                        placeholder="Kundennummer"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="gas_annual_consumption"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Vorjahresgasverbrauch in kWh *
                      </label>
                      <TextInput
                        id="gas_annual_consumption"
                        type="number"
                        value={gasDetails.annual_consumption_kwh}
                        onChange={(e) =>
                          setGasDetails({
                            ...gasDetails,
                            annual_consumption_kwh: e.target.value,
                          })
                        }
                        required
                        placeholder="z.B. 15000"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="gas_meter_point_id"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Zählpunktbezeichnung / ID der Marktlokation
                      </label>
                      <TextInput
                        id="gas_meter_point_id"
                        type="text"
                        value={gasDetails.meter_point_id}
                        onChange={(e) =>
                          setGasDetails({
                            ...gasDetails,
                            meter_point_id: e.target.value,
                          })
                        }
                        placeholder="Optional"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="gas_meter_number"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Eigentumsnummer des Gaszählers *
                      </label>
                      <TextInput
                        id="gas_meter_number"
                        type="text"
                        value={gasDetails.meter_number}
                        onChange={(e) =>
                          setGasDetails({
                            ...gasDetails,
                            meter_number: e.target.value,
                          })
                        }
                        required
                        placeholder="Zählernummer"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="gas_meter_reading"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Zählerstand
                      </label>
                      <TextInput
                        id="gas_meter_reading"
                        type="number"
                        value={gasDetails.meter_reading}
                        onChange={(e) =>
                          setGasDetails({
                            ...gasDetails,
                            meter_reading: e.target.value,
                          })
                        }
                        placeholder="Optional"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="gas_unit_price"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Arbeitspreis ct/kWh/brutto *
                      </label>
                      <TextInput
                        id="gas_unit_price"
                        type="number"
                        step="0.01"
                        value={gasDetails.unit_price_ct_kwh}
                        onChange={(e) =>
                          setGasDetails({
                            ...gasDetails,
                            unit_price_ct_kwh: e.target.value,
                          })
                        }
                        required
                        placeholder="z.B. 12.50"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="gas_base_price"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Grundpreis EUR/Monat/brutto *
                      </label>
                      <TextInput
                        id="gas_base_price"
                        type="number"
                        step="0.01"
                        value={gasDetails.base_price_eur_month}
                        onChange={(e) =>
                          setGasDetails({
                            ...gasDetails,
                            base_price_eur_month: e.target.value,
                          })
                        }
                        required
                        placeholder="z.B. 9.99"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="gas_delivery_start"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Nächstmöglicher Zeitpunkt Lieferbeginn *
                      </label>

                      {/* Checkbox for "as soon as possible" */}
                      <div className="mb-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={gasDetails.as_soon_as_possible}
                            onChange={(e) => {
                              setGasDetails({
                                ...gasDetails,
                                as_soon_as_possible: e.target.checked,
                                delivery_start_date: e.target.checked
                                  ? ""
                                  : gasDetails.delivery_start_date,
                              });
                              // Clear date error when checkbox is checked
                              if (e.target.checked) {
                                setGasDateError(null);
                              }
                            }}
                            className="mr-2 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Schnellstmöglich (kein spezifisches Datum)
                          </span>
                        </label>
                      </div>

                      {/* Date input field */}
                      <input
                        id="gas_delivery_start"
                        type="date"
                        value={gasDetails.delivery_start_date}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          setGasDetails({
                            ...gasDetails,
                            delivery_start_date: newDate,
                          });

                          // Validate date
                          if (newDate && !validateDeliveryDate(newDate)) {
                            setGasDateError(
                              "Das Lieferbeginn-Datum muss nach dem heutigen Datum liegen"
                            );
                          } else {
                            setGasDateError(null);
                          }
                        }}
                        required={!gasDetails.as_soon_as_possible}
                        disabled={gasDetails.as_soon_as_possible}
                        className={`block w-full rounded-lg border p-2.5 text-sm ${
                          gasDetails.as_soon_as_possible
                            ? "cursor-not-allowed bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500"
                            : gasDateError
                            ? "border-red-500 bg-red-50 text-red-900 focus:border-red-500 focus:ring-red-500 dark:border-red-400 dark:bg-red-900/20 dark:text-red-400"
                            : "border-gray-300 bg-gray-50 text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        } dark:placeholder:text-gray-400`}
                      />
                      {gasDateError && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          {gasDateError}
                        </p>
                      )}

                      {/* Show "schellst." indicator when checkbox is checked */}
                      {gasDetails.as_soon_as_possible && (
                        <div className="mt-2 rounded-md bg-blue-50 p-2 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                          ✓ Lieferbeginn: Schnellstmöglich
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="gas_monthly_payment"
                        className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        gewünschter monatl. Abschlag
                      </label>
                      <TextInput
                        id="gas_monthly_payment"
                        type="number"
                        step="0.01"
                        value={gasDetails.desired_monthly_payment}
                        onChange={(e) =>
                          setGasDetails({
                            ...gasDetails,
                            desired_monthly_payment: e.target.value,
                          })
                        }
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* Contract Types Grid */}
        <div className="mt-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h4 className="text-base font-medium text-gray-900 dark:text-white">
              Verfügbare Produkte
            </h4>
            <div className="w-full md:max-w-md">
              <div className="relative">
                <TextInput
                  id="search-contract-types"
                  type="text"
                  icon={HiSearch}
                  placeholder="Produkte durchsuchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    onClick={handleClearSearch}
                    aria-label="Suche löschen"
                  >
                    <HiX className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {!hasActiveProductFilters && !hasSearchInput ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Wählen Sie Kategorie, Unternehmen oder nutzen Sie die Suche.
            </p>
          ) : loadingTypes ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Produkte werden geladen...
            </p>
          ) : contractTypes.length > 0 ? (
            <>
              <div className="mb-3 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                {isGlobalSearchFallback && (
                  <p>
                    Keine Treffer in den ausgewählten Filtern. Ergebnisse aus
                    allen Kategorien und Unternehmen.
                  </p>
                )}
                <p>{totalContractTypes} Produkte gefunden</p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {contractTypes.map((contractType) => (
                  <ContractTypeCard
                    key={contractType.id}
                    contractType={contractType}
                    onAdd={() => addContractType(contractType)}
                    onRemove={() => removeContractType(contractType.id)}
                    isSelected={contractData.selectedContractTypes.some(
                      (ct) => ct.id === contractType.id
                    )}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-4 flex justify-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    showIcons
                  />
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {hasSearchInput
                ? `Keine Produkte gefunden für "${searchQuery}"`
                : "Keine Produkte für diese Filter verfügbar"}
            </p>
          )}
        </div>

        {/* Selected Contracts Section */}
        {contractData.selectedContractTypes.length > 0 && (
          <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-700">
            <h4 className="mb-4 text-base font-medium text-gray-900 dark:text-white">
              Ausgewählt ({contractData.selectedContractTypes.length})
            </h4>

            <div className="mb-4 flex flex-wrap gap-2">
              {contractData.selectedContractTypes.map((contractType) => {
                const companyName =
                  contractType.company_details?.company ?? "Unbekannt";
                const colorCode =
                  contractType.company_details?.color_code ?? "#6B7280";

                return (
                  <Badge
                    key={contractType.id}
                    color="info"
                    className="flex items-center gap-2 px-3 py-1.5"
                    style={{ backgroundColor: colorCode }}
                  >
                    <span>
                      {contractType.type || "Unbekannt"} - {companyName}
                    </span>
                    <button
                      onClick={() => removeContractType(contractType.id)}
                      className="ml-1 rounded-full bg-white/20 p-0.5 hover:bg-white/40"
                      aria-label={`Entfernen von ${
                        contractType.type || "Produkt"
                      }`}
                    >
                      <HiX className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-4">
                <Button color="light" onClick={resetForm}>
                  Formular zurücksetzen
                </Button>
                {!contractData.contract.signature_path ? (
                  <Button
                    color="primary"
                    onClick={() => setShowSignaturePad(true)}
                    disabled={isBirthDateMissing()}
                  >
                    Unterschreiben
                  </Button>
                ) : (
                  <Button
                    color="success"
                    onClick={handleSaveClick}
                    disabled={isSaving}
                  >
                    {isSaving ? "Wird gespeichert..." : "Kunden speichern"}
                  </Button>
                )}
              </div>
              {/* Alert for missing birth date */}
              {isBirthDateMissing() && (
                <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  Geburtsdatum erforderlich
                </div>
              )}
              {saveError && (
                <Alert color="failure" className="mt-2">
                  {saveError}
                </Alert>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Signature Pad Modal */}
      {showSignaturePad && (
        <SignaturePad
          onSave={handleSignatureSave}
          onCancel={() => setShowSignaturePad(false)}
        />
      )}

      {/* Confirmation Modal */}
      <ContractConfirmationModal
        show={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleSaveContract}
        isLoading={isSaving}
        contractData={contractData}
        address={tempAddress}
      />

      {/* Success/Error Modal for PDF and Email */}
      <ContractSuccessModal
        show={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          setSuccessModalActionLabel(undefined);
        }}
        success={successModalSuccess}
        message={successModalMessage}
        actionLabel={successModalActionLabel}
        autoCloseDelay={successModalSuccess ? 3000 : 0}
      />
    </div>
  );
};

export default ContractCard;
